import * as yup from 'yup';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  Slide,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import React, { FC, useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useVerifiedOffer,
  useVerifiedOfferBin,
  useVerifiedUnipalOffer,
} from '@/logic/state/offers';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  MuviError,
  UnipalVerifyResponseModel,
  useCheckVerifyPhoneNumber,
} from '@/logic/services';
import OfferBadge from './offer-badge';
import OfferLottie from '../../lotties/muvi_offer.json';
import { OfferTermsAndConditions } from './offer-terms-and-conditions';
import { preventInvalidChars } from '@alpha.apps/react-common';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useBookingFlowCity } from '@/logic/hooks';
import { useDialog } from '@/logic/state/dialog';
import { useOfferHandler } from '@/logic/hooks';
import { useRouter } from 'next/router';
import { useVerifyOffer } from '@/logic/services/offers';
import { CloseButton } from '../shared/close-button';
import ImageBoxFuture from '../shared/image-box-future';
import { DateTime } from 'luxon';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  acceptOnlyNumberOnSafari,
  aN2eN,
  checkIfPhoneNumberIsValid,
  country,
} from '@/logic/shared';
import { excludedCountriesList } from '@/constants/phone-numbers';
import dynamic from 'next/dynamic';
import { preventEmptyString } from '@/constants/regular-expressions';
import { CreditCardExpiryDateInput } from '../shared/add-credit-card/credit-card-expiry-date-input';

const CheckBoxLabel = (): JSX.Element => {
  return (
    <Typography
      component='div'
      sx={theme => ({
        color: theme.palette.typography.color.muted,
        fontWeight: 400,
        whiteSpace: 'nowrap',
        fontSize: { tablet: pxToRem(13), mobile: '14px' },
      })}
    >
      <FormattedMessage id='global.i_agree' />
    </Typography>
  );
};

const MuiPhoneNumber = dynamic(
  () => import('material-ui-phone-number').then(component => component.default),
  { ssr: false }
);

export const ApplyOfferDialog: FC = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { formatMessage } = useIntl();
  const [AgreeOnTerms, setAgreeOnTerms] = useState<boolean>(false);
  const { push } = useRouter();
  const { cityId } = useBookingFlowCity();
  const [countryCode_contact, setCountryCode_contact] = useState<string>('KSA');
  const [selectedCountry, setSelectedCountry] = useState<country>(null);
  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const [isShowTC, setIsShowTC] = useState(false);

  const { offer, movieId, movieSlug, sessionId, isAfterMidnight, isThreeD } =
    dialog?.applyOfferDialogPayload;

  const [verifiedOffer, setVerifiedOffer] = useVerifiedOffer(
    s => [s.verifiedOffer, s.setVerifiedOffer],
    shallow
  );
  const [
    setVerifiedOfferBin,
    setVerifiedOfferLast4Bin,
    setVerifiedOfferExpiryMonth,
    setVerifiedOfferExpiryYear,
  ] = useVerifiedOfferBin(
    s => [
      s.setVerifiedOfferBin,
      s.setVerifiedOfferLast4Bin,
      s.setVerifiedOfferExpiryMonth,
      s.setVerifiedOfferExpiryYear,
    ],
    shallow
  );
  const [setVerifiedUnipalOffer] = useVerifiedUnipalOffer(
    s => [s.setVerifiedUnipalOffer],
    shallow
  );
  const {
    isBankOffer,
    isOfferVerified,
    offerTitle,
    handleDeleteOffer,
    isUnipalOffer,
    isNoneOffer,
  } = useOfferHandler(offer);
  const checkPhoneNumber = useCheckVerifyPhoneNumber();

  const [binNumber, setBinNumber] = useState('');
  const [last4BinNumber, setLast4BinNumber] = useState('');

  const last4BinNumberRef = useRef<HTMLInputElement>(null);

  const canSelectSeats =
    isNoneOffer || ((isBankOffer || isUnipalOffer) && isOfferVerified);

  const schema = yup.object({
    expiryDate: yup.date().when('isBankOffer', {
      is: () => isBankOffer,
      then: yup
        .date()
        .required()
        .min(
          DateTime.now().toFormat('yyyy-MM'),
          intl.formatMessage({ id: 'my_credit_cards.expiry_date.invalid' })
        )
        .max(
          DateTime.now().plus({ years: 20 }).toFormat('yyyy-MM'),
          intl.formatMessage({ id: 'my_credit_cards.expiry_date.invalid' })
        )
        .typeError(intl.formatMessage({ id: 'my_credit_cards.expiry_date.invalid' }))
        .nullable(),
    }),
    unipalPhoneNumber: yup.string().when('isUnipalOffer', {
      is: () => isUnipalOffer,
      then: yup
        .string()
        .test(
          'test-valid-phone',
          intl.formatMessage({ id: 'global.phoneNumber.notValid' }),
          value => {
            if (!!value) {
              return checkIfPhoneNumberIsValid(value, countryCode_contact);
            }
          }
        ),
    }),
    unipalCode: yup.string().when('isUnipalOffer', {
      is: () => isUnipalOffer,
      then: yup
        .string()
        .test(
          'test-unipal-code',
          intl.formatMessage({ id: 'offers.unipal.errors' }),
          value => preventEmptyString.test(value)
        ),
    }),
  });

  const {
    getValues,
    setValue,
    register,
    trigger,
    setError,
    formState: { errors },
  } = useForm<{
    expiryDate?: string | null;
    unipalPhoneNumber?: string;
    unipalCode?: string;
  }>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      expiryDate: null,
      unipalPhoneNumber: '',
      unipalCode: '',
    },
  });

  const expiryDateArr = getValues('expiryDate')?.split('-');
  const expiryMonth = expiryDateArr?.length > 0 ? expiryDateArr[1] : null;
  const expiryYear = expiryDateArr?.length > 0 ? expiryDateArr[0] : null;

  const { error, isError, isLoading, mutate } = useVerifyOffer(
    {},
    {
      onError: () => {
        if (verifiedOffer?.id) {
          setVerifiedOffer(null);
        }
      },
    }
  );

  const handleBackwardClick = useCallback(() => {
    setIsShowTC(false);
  }, []);

  const openTermsAndConditionsDialog = useCallback(() => {
    setIsShowTC(true);
  }, []);

  const handleVerifyBin = () => {
    mutate(
      {
        query: {
          offerId: offer?.id,
        },
        params: {
          bin: binNumber,
          last4: last4BinNumber,
          expiryMonth,
          expiryYear,
          cityId,
          ...(sessionId ? { sessionId } : {}),
        },
      },
      {
        onSuccess: () => {
          setVerifiedOffer({ id: offer?.id });
          setVerifiedOfferBin(binNumber);
          setVerifiedOfferLast4Bin(last4BinNumber);
          setVerifiedOfferExpiryMonth(expiryMonth);
          setVerifiedOfferExpiryYear(expiryYear);
        },
      }
    );
  };

  const handleVerifyUnipal = () => {
    mutate(
      {
        query: {
          offerId: offer?.id,
        },
        params: {
          code: getValues()?.unipalCode,
          phoneNumber: getValues()?.unipalPhoneNumber,
          cityId,
          ...(sessionId ? { sessionId } : {}),
        },
      },
      {
        onSuccess: (res: UnipalVerifyResponseModel) => {
          setVerifiedOffer({ id: offer?.id });
          setVerifiedUnipalOffer({
            unipalStudentId: res?.referenceId,
            unipalSessionId: res?.unipalSessionId,
            unipalPhoneNumber: getValues()?.unipalPhoneNumber,
            unipalCode: getValues()?.unipalCode,
          });
        },
      }
    );
  };

  const handleApplyOffer = () => {
    setVerifiedOffer({
      id: offer?.id,
      isBankOfferApplied: isBankOffer,
      isUnipalOfferApplied: isUnipalOffer,
    });

    // accessed from movie details
    if (!sessionId) {
      push(`/movies/${movieSlug}?offerId=${offer?.id}&cityId=${cityId}`);
      setDialog({});
      return;
    }

    setDialog({
      wrapper: true,
      isRulesOfEntryDialog: true,
      rulesOfEntryDialogPayload: {
        sessionId,
        movieId,
        movieSlug,
        isAfterMidnight,
        isThreeD,
        offerId: offer?.id,
      },
    });
  };

  const regex = /^[0-9]*$/;

  const handleChangeBin = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (regex.test(e.target.value) && e.target.value.length <= offer?.binLimit) {
      setBinNumber(e.target.value);
      if (e.target.value.length === offer?.binLimit) {
        last4BinNumberRef.current?.focus();
      }
    }
  };

  const handleChangeLast4Bin = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= 4) {
      setLast4BinNumber(e.target.value);
    }
  };

  const handleCloseDialog = () => {
    setDialog({});
  };

  return (
    <>
      {!isShowTC && (
        <>
          <DialogTitle>
            <CloseButton handleClick={handleCloseDialog} />
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                margin: 'auto',
              }}
            >
              <Typography
                component='h4'
                sx={{
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: { tablet: pxToRem(22), mobile: '18px' },
                  mb: { tablet: pxToRem(32), mobile: '24px' },
                  mt: { tablet: pxToRem(30), mobile: '24px' },
                  color: theme.palette.grey[90],
                  width: '100%',
                }}
              >
                <FormattedMessage id='offers.apply_offer' />
              </Typography>

              <Box
                component='div'
                sx={{
                  mb: { tablet: pxToRem(16), mobile: '24px' },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {offer?.logoUrl && (
                  <ImageBoxFuture
                    img={offer?.logoUrl}
                    title={
                      offer?.logoUrlAlt ||
                      intl.formatMessage({
                        id: 'alt.offer_logo',
                      })
                    }
                    layout='intrinsic'
                    objectFit='contain'
                    width='524px'
                    height='294px'
                    sx={{
                      width: {
                        mobile: '92px',
                        tablet: pxToRem(100),
                      },
                      marginRight: {
                        mobile: '16px',
                        tablet: pxToRem(16),
                      },
                      borderRadius: '5px',
                      overflow: 'hidden',
                    }}
                  />
                )}
                <Box component='div'>
                  <Typography
                    component='h5'
                    sx={{
                      fontWeight: 700,
                      fontSize: { tablet: pxToRem(16), mobile: '14px' },
                      color: theme.palette.grey[80],
                    }}
                  >
                    {isBankOffer ? offer?.bankName : offer?.title}
                  </Typography>
                  <Typography
                    component='p'
                    sx={{
                      fontSize: { tablet: pxToRem(14), mobile: '13px' },
                      color: theme.palette.grey[50],
                    }}
                  >
                    {isBankOffer ? offer?.title : offer?.hint}
                  </Typography>
                </Box>
              </Box>
              {isUnipalOffer && !isOfferVerified && (
                <>
                  <Typography
                    component='p'
                    sx={{
                      fontSize: {
                        mobile: '14px',
                        tablet: pxToRem(16),
                      },
                      color: theme.palette.grey[60],
                      marginBottom: {
                        mobile: '16px',
                        tablet: pxToRem(20),
                      },
                    }}
                  >
                    <FormattedMessage id={'offers.unipal.description'} />
                  </Typography>
                  <MuiPhoneNumber
                    excludeCountries={excludedCountriesList}
                    id='offer-unipal-phone-number'
                    defaultCountry={'sa'}
                    dir='ltr'
                    label={<FormattedMessage id='global.phone_number' />}
                    variant={'outlined'}
                    onChange={(e, country?: country) => {
                      setError('unipalPhoneNumber', {});
                      setSelectedCountry(country);
                      const field = 'unipalPhoneNumber';
                      const c = country?.countryCode?.toString().toUpperCase();
                      if (field.includes('contact')) {
                        setCountryCode_contact(c);
                      }
                      setValue('unipalPhoneNumber', e);
                      trigger('unipalPhoneNumber');
                    }}
                    value={getValues()?.unipalPhoneNumber || ''}
                    error={Boolean(errors?.unipalPhoneNumber)}
                    disabled={checkPhoneNumber?.isLoading}
                    InputProps={{
                      autoComplete: 'off',
                    }}
                    sx={{
                      mb: 2.5,
                      ' & .MuiButtonBase-root ': {
                        '& svg': { height: '1em' },
                      },
                      legend: {
                        width: 0,
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        backgroundColor: 'white',
                        paddingInline: '6px',
                      },
                    }}
                  />
                  <TextField
                    id='offer-unipal-code'
                    type='text'
                    label={<FormattedMessage id={'offers.unipal.code'} />}
                    disabled={isLoading}
                    variant='outlined'
                    onChange={e => {
                      setValue('unipalCode', e.target.value);
                      trigger('unipalCode');
                    }}
                    value={getValues()?.unipalCode}
                    sx={{
                      width: '100%',
                      fontSize: { tablet: pxToRem(16), mobile: '16px' },
                      color: theme.palette.grey[50],
                      '& input': {
                        p: '15px',
                      },
                    }}
                    error={!!errors?.unipalCode}
                  />
                  {(isError || errors?.unipalPhoneNumber || errors?.unipalCode) && (
                    <FormHelperText
                      error
                      sx={{
                        fontSize: {
                          mobile: '14px',
                          tablet: pxToRem(16),
                        },
                      }}
                    >
                      {(error as MuviError)?.response?.data?.message ||
                        (errors?.unipalPhoneNumber?.message as string) ||
                        (errors?.unipalCode?.message as string)}
                    </FormHelperText>
                  )}
                </>
              )}
              {isBankOffer && !isOfferVerified && (
                <>
                  <Typography
                    component='p'
                    sx={{
                      fontSize: {
                        mobile: '14px',
                        tablet: pxToRem(16),
                      },
                      color: theme.palette.grey[60],
                      marginBottom: {
                        mobile: '16px',
                        tablet: pxToRem(20),
                      },
                    }}
                  >
                    <FormattedMessage id='offers.enter_the_first' />
                    &nbsp;
                    {offer?.binLimit}
                    &nbsp;
                    <FormattedMessage id='offers.digits' />
                    &nbsp;
                    <FormattedMessage id='offers.enter_the_last' />
                  </Typography>

                  <Grid
                    container
                    dir='ltr'
                    rowSpacing={{
                      mobile: '8px',
                      tablet: pxToRem(8),
                    }}
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Grid item mobile={12} tablet={12}>
                      <Stack
                        direction='row'
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <TextField
                          id='offer-verified-number'
                          type='number'
                          inputMode='numeric'
                          label={<FormattedMessage id='my_credit_cards.card_number' />}
                          placeholder={
                            offer?.binLimit === 4
                              ? '1234'
                              : offer?.binLimit === 6
                              ? '123456'
                              : '12345678'
                          }
                          disabled={isLoading}
                          variant='outlined'
                          value={binNumber}
                          onKeyDown={e => {
                            preventInvalidChars(e, ['e', 'E', '+', '-', '.']);
                            acceptOnlyNumberOnSafari(e);
                          }}
                          onChange={handleChangeBin}
                          sx={{
                            width: '100%',
                            fontSize: { tablet: pxToRem(16), mobile: '16px' },
                            color: theme.palette.grey[50],
                            '& input': {
                              p: '15px',
                            },
                          }}
                          error={isError}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            inputMode: 'numeric',
                          }}
                        />

                        <Box
                          component='span'
                          sx={{
                            marginX: {
                              mobile: '8px',
                              tablet: pxToRem(8),
                            },
                            color: theme.palette.typography.color.muted,
                          }}
                        >
                          ******
                        </Box>

                        <TextField
                          id='offer-verified-last4-number'
                          type='number'
                          inputMode='numeric'
                          placeholder='1234'
                          inputRef={last4BinNumberRef}
                          disabled={isLoading}
                          variant='outlined'
                          value={last4BinNumber}
                          onKeyDown={e => {
                            preventInvalidChars(e, ['e', 'E', '+', '-', '.']);
                            acceptOnlyNumberOnSafari(e);
                          }}
                          onChange={handleChangeLast4Bin}
                          sx={{
                            width: '100%',
                            fontSize: { tablet: pxToRem(16), mobile: '16px' },
                            color: theme.palette.grey[50],
                            '& input': {
                              p: '15px',
                            },
                          }}
                          error={isError}
                        />
                      </Stack>
                      <Grid item mobile={12} tablet={12}>
                        <Stack
                          sx={{
                            width: '100%',
                            marginY: { mobile: '16px', tablet: pxToRem(32) },
                          }}
                        >
                          <CreditCardExpiryDateInput
                            isLoading={isLoading}
                            isError={
                              (errors.expiryDate?.type &&
                                errors.expiryDate?.type !== 'required') ||
                              isError
                            }
                            helperText={errors.expiryDate?.message as string}
                            value={getValues()?.expiryDate || null}
                            registerFunc={() => register('expiryDate')}
                            handleChange={e => {
                              e && setValue('expiryDate', aN2eN(e?.toFormat('yyyy-MM')));
                              trigger();
                            }}
                          />
                          {isError && (
                            <FormHelperText
                              error
                              sx={{
                                fontSize: {
                                  mobile: '14px',
                                  tablet: pxToRem(16),
                                },
                              }}
                            >
                              {(error as MuviError)?.response?.data?.message}
                            </FormHelperText>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}

              {(isBankOffer || isUnipalOffer) && isOfferVerified && (
                <OfferBadge
                  title={formatMessage({ id: 'offer.badge.applied' })}
                  subTitle={offerTitle}
                  lottie={OfferLottie}
                  onDelete={() => {
                    handleDeleteOffer();
                    setBinNumber('');
                    setLast4BinNumber('');
                    setValue('expiryDate', null);
                    setValue('unipalPhoneNumber', '');
                    setValue('unipalCode', '');
                    setAgreeOnTerms(false);
                  }}
                  styles={{
                    subTitle: {
                      color: theme.palette.grey[60],
                    },
                  }}
                />
              )}
              {canSelectSeats && (
                // {(!isBankOffer || (isBankOffer && isOfferVerified)) && (
                <>
                  <Typography
                    component='p'
                    sx={{
                      fontSize: {
                        mobile: '14px',
                        tablet: pxToRem(16),
                      },
                      mt: 3,
                      color: theme.palette.grey[60],
                    }}
                  >
                    <FormattedMessage id='offers.check_terms_and_condition' />
                  </Typography>

                  <Box sx={{ width: '100%' }}>
                    <Stack
                      direction='row'
                      sx={{
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        fontSize: { tablet: pxToRem(14), mobile: '14px' },
                      }}
                    >
                      <FormControlLabel
                        sx={{
                          marginRight: '0px',
                        }}
                        control={
                          <Checkbox
                            id='offer-terms-input'
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setAgreeOnTerms(e.target.checked)
                            }
                            checked={AgreeOnTerms}
                          />
                        }
                        label={<CheckBoxLabel />}
                      />
                      <Box component='span'>&nbsp;</Box>
                      <Button
                        id='view-offer-terms-button'
                        sx={{
                          fontFamily: theme.palette.typography.font.bold,
                          color: theme.palette.link.main,
                          fontSize: { tablet: pxToRem(13), mobile: '14px' },
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                        }}
                        onClick={() => openTermsAndConditionsDialog()}
                      >
                        <FormattedMessage id='terms.and.conditions' />
                      </Button>
                    </Stack>
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            {canSelectSeats ? (
              // {!isBankOffer || (isBankOffer && isOfferVerified) ? (
              <LoadingButton
                id='apply-offer-and-select-seats'
                type='button'
                fullWidth
                disabled={
                  !AgreeOnTerms || ((isBankOffer || isUnipalOffer) && !isOfferVerified)
                }
                variant='contained'
                onClick={handleApplyOffer}
                sx={{
                  fontSize: { tablet: pxToRem(16), mobile: '16px', fontWight: 'bold' },
                  height: { tablet: pxToRem(56), mobile: '56px' },
                }}
              >
                <FormattedMessage id='offers.apply_and_select_seats' />
              </LoadingButton>
            ) : (
              <LoadingButton
                type='button'
                id='verify-offer-button'
                fullWidth
                disabled={
                  !offer?.id ||
                  (isBankOffer &&
                    (binNumber?.length < offer?.binLimit ||
                      last4BinNumber?.length < 4 ||
                      !getValues()?.expiryDate)) ||
                  (isUnipalOffer &&
                    (!getValues()?.unipalCode?.trim() ||
                      !getValues()?.unipalPhoneNumber ||
                      !!errors?.unipalPhoneNumber))
                }
                variant='contained'
                onClick={isBankOffer ? handleVerifyBin : handleVerifyUnipal}
                loading={isLoading}
                sx={{
                  fontSize: { tablet: pxToRem(16), mobile: '16px', fontWight: 'bold' },
                  height: { tablet: pxToRem(56), mobile: '56px' },
                }}
              >
                <FormattedMessage id='global.verify' />
              </LoadingButton>
            )}
          </DialogActions>
        </>
      )}

      <Slide
        appear={false}
        direction='left'
        in={isShowTC}
        unmountOnExit
        mountOnEnter
        exit={false}
      >
        <Box>
          <OfferTermsAndConditions
            termsConditions={offer?.termsConditions}
            handleBackwardClick={handleBackwardClick}
          />
        </Box>
      </Slide>
    </>
  );
};
